import React, { useState } from 'react';

import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import Heading from 'components/partials/heading/heading';
import Loader from 'components/partials/loader/loader';
import Select from 'components/partials/select/select';

import GoalItem from 'containers/user/goals/goal-item';
import GoalContactItem from 'containers/user/goals/goal-contact-item';

import { generateYearOptions } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-filters/goals-matrix-filters';
import type { YearOption } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-filters/goals-matrix-filters';

import { getCurrentYear } from 'helpers/date.helpers';
import useGoals from 'containers/user/goals/use-goals';
import type { Goal } from 'containers/user/goals/use-goals';
import { ROWS } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-tab.constants';

const MyGoals = (): React.JSX.Element => {
  const currentYear = getCurrentYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const years = generateYearOptions({
    futureYears: 1,
    pastYears: 1,
  });

  const { loading, goals } = useGoals(selectedYear);

  const groupGoalsByCategory = (goalsList: Goal[]): Record<string, Goal[]> =>
    goalsList.reduce<Record<string, Goal[]>>((acc, goal) => {
      const category = goal.category;

      if (category === 'Other') return acc;

      const hasEthnicity = goal.details?.some(
        (detail) => detail.focusArea === 'Ethnicity',
      );

      if (hasEthnicity) return acc;

      const cat = category as string;

      if (!acc[cat]) {
        acc[cat] = [];
      }
      acc[cat].push(goal);

      return acc;
    }, {});

  const renderGoalsList = (): React.JSX.Element => {
    const goalsByCategory = groupGoalsByCategory(goals);
    const categories = Object.keys(goalsByCategory);

    const orderMap = Object.values(ROWS).reduce<Record<string, number>>(
      (map, key, index) => {
        map[key as string] = index;

        return map;
      },
      {},
    );

    const sortedCategories = categories.toSorted(
      (a: string, b: string) => (orderMap[a] ?? 0) - (orderMap[b] ?? 0)
    );

    return (
      <div className="mt-4">
        {sortedCategories.map((category) => (
          <div
            key={category}
            className="mb-6 flex flex-col mobile:items-center tablet:items-start"
          >
            <Heading variant="h2" customClass="font-bold">
              {category} category
            </Heading>
            <div className="flex flex-wrap gap-4 mt-2 mobile:justify-center tablet:justify-start">
              {goalsByCategory[category].map((goal) => (
                <GoalItem
                  key={goal.id}
                  goal={goal}
                  isCurrentFiscalYear={selectedYear === currentYear}
                />
              ))}
            </div>
          </div>
        ))}
        <GoalContactItem />
      </div>
    );
  };

  return (
    <ResponsiveWrapper customClass="mt-8">
      <Loader loading={loading} fullScreen={false} />
      <div className="flex items-center mb-6 gap-4 mobile:justify-center tablet:justify-start">
        <Heading variant="h1">My Goals</Heading>
        <Select<YearOption>
          options={years}
          value={years.find((year) => year.value === selectedYear)}
          onChange={(option) => {
            if (option && !Array.isArray(option)) {
              setSelectedYear(option.value);
            }
          }}
          selectWrapperClass="!w-28 mr-4"
          extraBasicStyles={{ padding: 0, height: '2' }}
          aria-label="Select year"
        />
      </div>
      <p className="text-lg mobile:text-center mobile:w-full md:w-1/2 tablet:text-left">
        Your goals and Critical Elements for achieving them are outlined below.
        MARKETview keeps you continuously updated on your progress by measuring
        and monitoring your performance daily.
      </p>

      {renderGoalsList()}
    </ResponsiveWrapper>
  );
};

export default MyGoals;
